import type {
  ReportingDemographicsDimension,
  ReportingDemographicsResponse,
  ReportingDemographicsRow,
} from '../../types/reporting';
import { formatCurrencyAmounts } from '../../lib/reportingMoney';
import { toLocalYmd } from '../../lib/reportingDateRange';
import {
  demographicsDenominatorNote,
  demographicsEmptyMessage,
  demographicsPeriodNote,
} from '../../lib/reporting/demographics';
import {
  demographicsAsOfNote,
  dimensionLabel,
  dimensionValueLabel,
  displayableDimensions,
  formatCount,
  formatShare,
} from './demographicsView';

// The Meta AUDIENCE panel — who responded to this account's ads, by age, gender,
// region, platform and device.
//
// ⚠️ DELIBERATELY PLAIN. The portal restyle is the next piece of work and will
// dress this; what has to be right NOW is the display LOGIC — which cuts, how
// they are aggregated, what is suppressed, and how the period is labelled. That
// survives a restyle; styling does not.
//
// ⚠️ EVERY SENTENCE ON THIS PANEL COMES FROM A PURE FUNCTION. The period note,
// the empty-state reasons, the denominator caveat and the floor explanations all
// live in src/lib/reporting/demographics.ts, and the share/count formatting in
// demographicsView.ts, so each is unit-tested without jsdom. This file arranges
// them; it does not decide anything.

/** dd MMM — short, because these appear inline in sentences. */
function formatDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', timeZone: 'UTC' });
}

function formatInstant(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function Row({ row, dimension }: { row: ReportingDemographicsRow; dimension: string }) {
  return (
    <tr className="border-t border-slate-100">
      <th scope="row" className="py-1.5 pr-3 text-left font-normal">
        {dimensionValueLabel(dimension, row.value)}
      </th>
      <td className="py-1.5 pr-3 text-right tabular-nums">{formatCount(row.leads)}</td>
      <td className="py-1.5 pr-3 text-right tabular-nums">{formatShare(row.leadShare)}</td>
      <td className="py-1.5 pr-3 text-right tabular-nums">{formatCount(row.impressions)}</td>
      <td className="py-1.5 pr-3 text-right tabular-nums">{formatShare(row.impressionShare)}</td>
      <td className="py-1.5 text-right tabular-nums">{formatCurrencyAmounts(row.spend)}</td>
    </tr>
  );
}

function DimensionTable({ dimension }: { dimension: ReportingDemographicsDimension }) {
  return (
    <section className="mb-8">
      <h3 className="mb-1 text-sm font-semibold text-foreground">
        {dimensionLabel(dimension.dimension)}
      </h3>
      {/* ⚠️ RULE 3, ABOVE THE FIGURES. The denominator and the fact that it does
          not reconcile to the headline are stated BEFORE the numbers — a caveat
          read after a number has been believed is not a caveat. */}
      <p className="mb-2 max-w-3xl text-xs text-slate-500">
        {demographicsDenominatorNote(dimension)}
      </p>
      {/* ⚠️ NO FLOOR NOTE — there are no floors. Every share is shown at every
          size, and the denominator note above is what makes that honest. */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[36rem] text-sm">
          <thead>
            <tr className="text-xs uppercase tracking-wide text-slate-400">
              <th scope="col" className="py-1 pr-3 text-left font-medium">
                {dimensionLabel(dimension.dimension)}
              </th>
              <th scope="col" className="py-1 pr-3 text-right font-medium">
                Leads
              </th>
              <th scope="col" className="py-1 pr-3 text-right font-medium">
                Lead share
              </th>
              <th scope="col" className="py-1 pr-3 text-right font-medium">
                Impressions
              </th>
              <th scope="col" className="py-1 pr-3 text-right font-medium">
                Impr. share
              </th>
              <th scope="col" className="py-1 text-right font-medium">
                Spend
              </th>
            </tr>
          </thead>
          <tbody>
            {/* ⚠️ EVERY cell, including a one-lead one. The floors withhold a
                SHARE (rendered as a dash by formatShare), never a row — so the
                rows below sum to the Total line exactly, and a reader can check
                the table against its own stated denominator. */}
            {dimension.rows.map((row) => (
              <Row key={row.value} row={row} dimension={dimension.dimension} />
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-slate-300 font-medium">
              <th scope="row" className="py-1.5 pr-3 text-left">
                Total
              </th>
              <td className="py-1.5 pr-3 text-right tabular-nums">
                {formatCount(dimension.totals.leads)}
              </td>
              <td className="py-1.5 pr-3" />
              <td className="py-1.5 pr-3 text-right tabular-nums">
                {formatCount(dimension.totals.impressions)}
              </td>
              <td className="py-1.5 pr-3" />
              <td className="py-1.5 text-right tabular-nums">
                {formatCurrencyAmounts(dimension.totals.spend)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  );
}

export function DemographicsPanel({
  data,
  isLoading,
  error,
}: {
  data: ReportingDemographicsResponse | undefined;
  isLoading: boolean;
  error?: unknown;
}) {
  if (error) {
    return (
      <p className="max-w-3xl text-sm text-amber-700">
        couldn&apos;t load the audience breakdown.{' '}
        {error instanceof Error ? error.message : 'unknown error'}
      </p>
    );
  }
  if (isLoading || !data) {
    return <div className="h-40 animate-pulse rounded-md bg-slate-50" aria-hidden />;
  }

  // ⚠️ The VIEWER'S local today, so a stored window whose end was padded into
  // tomorrow is stated as ending today rather than in the future. `toLocalYmd`
  // rather than toISOString(): the UTC date names yesterday for a Brisbane
  // reader all morning, which would clamp the window a day short every day.
  const period = demographicsPeriodNote(
    data.period,
    data.range,
    formatDate,
    toLocalYmd(new Date()),
  );
  const dimensions = displayableDimensions(data.dimensions);

  return (
    <div>
      {/* ⚠️⚠️ THE PERIOD LEADS. On the fallback path these figures cover a WIDER
          period than the range selected above, and every other panel on the page
          says the selected range. Stating it after the tables would be stating it
          too late. */}
      {period ? (
        <p className="mb-1 max-w-3xl text-sm text-slate-600">
          <span className="font-medium">Audience data</span> {period}
        </p>
      ) : null}
      <p className="mb-6 max-w-3xl text-xs text-slate-500">
        {demographicsAsOfNote(data.asOf, formatInstant)}
      </p>

      {data.period.grain === 'none' ? (
        <p className="max-w-3xl text-sm text-slate-500">
          {demographicsEmptyMessage(data.period.reason, data.range, formatDate)}
        </p>
      ) : dimensions.length === 0 ? (
        // Reached only when a window WAS chosen but carried no cells — rare, and
        // a different statement from "no window covers this range".
        <p className="max-w-3xl text-sm text-slate-500">
          this window has no audience breakdown rows.
        </p>
      ) : (
        dimensions.map((d) => <DimensionTable key={d.dimension} dimension={d} />)
      )}
    </div>
  );
}
